import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import ImageViewer from '../components/ImageViewer';
import { planShareApi } from '../services/api';

const PublicPlanShare = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [selectedPin, setSelectedPin] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const res = await planShareApi.getSharedPlan(token);
        if (!cancelled) {
          setPayload(res.data);
        }
      } catch (e) {
        console.error('Failed to load shared plan:', e);
        if (!cancelled) {
          setError('This share link is invalid or has expired.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const image = useMemo(() => {
    if (!payload?.file || !payload?.file_url) return null;
    return [{
      file_id: payload.file.file_id,
      file_name: payload.file.file_name,
      uploaded_date: payload.file.uploaded_date,
      url: payload.file_url,
    }];
  }, [payload]);

  const resolvePublicPhotoUrl = useCallback(async (photo) => {
    if (!photo?.photo_id || !photo?.photo_type) return null;
    const res = await planShareApi.getPhotoUrl(token, photo.photo_id, photo.photo_type);
    return res.data?.url || null;
  }, [token]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !payload || !image) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Unable to load plan
        </Typography>
        <Typography color="text.secondary">
          {error || 'This share link is invalid or has expired.'}
        </Typography>
      </Box>
    );
  }

  return (
    <ImageViewer
      open={true}
      onClose={() => navigate('/')}
      imageUrl={payload.file_url}
      fileName={payload.file?.file_name || 'Plan'}
      uploadedDate={payload.file?.uploaded_date}
      allImages={image}
      currentIndex={0}
      onNavigate={null}
      pins={payload.pins || []}
      enablePinMode={false}
      onPinClick={(pin) => setSelectedPin(pin)}
      onAddPin={null}
      onTogglePinMode={null}
      isPlanImage={true}
      selectedPin={selectedPin}
      onClosePinDetails={() => setSelectedPin(null)}
      workers={[]}
      onEditActivity={null}
      onDeleteActivity={null}
      onDelete={null}
      isLoading={false}
      readOnly={true}
      resolvePublicPhotoUrl={resolvePublicPhotoUrl}
    />
  );
};

export default PublicPlanShare;
